import { forwardRef, type PropsWithChildren, useRef } from "react";
import {
  ResponsiveContainer,
  StackedCarousel as Carousel,
} from "react-stacked-center-carousel";

import ChevronIcon from "$/lib/icons/ChevronIcon";
import { cn } from "$/lib/utils/functions/misc.functions";

import type { StackedCarouselProps } from "./StackedCarousel.types";
import { StackedCarouselSlide } from "./StackedCarouselSlide";

const StackedCarousel = forwardRef<
  HTMLDivElement,
  PropsWithChildren<StackedCarouselProps>
>(
  (
    { containerClassName, carouselClassName, data, ...carouselProps },
    forwardedRef,
  ) => {
    const ref = useRef<Carousel | undefined>();

    return (
      <div
        ref={forwardedRef}
        className={cn(
          containerClassName,
          "relative h-[400px] w-full max-sm:h-[300px]",
        )}
      >
        <ResponsiveContainer
          carouselRef={ref}
          render={(parentWidth, carouselRef) => {
            let currentVisibleSlide = 5;

            if (parentWidth <= 1080) currentVisibleSlide = 3;
            if (parentWidth <= 768) currentVisibleSlide = 1;

            return (
              <Carousel
                ref={carouselRef}
                slideComponent={StackedCarouselSlide}
                slideWidth={parentWidth < 800 ? parentWidth - 40 : 750}
                carouselWidth={parentWidth}
                data={data}
                currentVisibleSlide={currentVisibleSlide}
                maxVisibleSlide={5}
                className={cn(carouselClassName, "h-[400px] max-sm:h-[300px]")}
                useGrabCursor
                {...carouselProps}
              />
            );
          }}
        />

        <button
          className="absolute left-2 top-1/2 z-10 -translate-y-1/2"
          onClick={() => {
            ref.current?.goBack();
          }}
        >
          <ChevronIcon
            width={18}
            height={18}
            color="black"
            className="rotate-180"
          />
        </button>

        <button
          className="absolute right-2 top-1/2 z-10 -translate-y-1/2"
          onClick={() => {
            ref.current?.goNext();
          }}
        >
          <ChevronIcon width={18} height={18} color="black" />
        </button>
      </div>
    );
  },
);

StackedCarousel.displayName = "StackedCarousel";

export default StackedCarousel;
